
import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import CollaborativeEditor from "./pages/CollaborativeEditor";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import LegalGuides from "./pages/LegalGuides";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MobileBrowser from "@/components/MobileBrowser";
import { useIsMobile } from "@/hooks/use-mobile";

// Create query client outside component to prevent recreation on renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Detect mobile browser type for specific optimizations
const getMobileOS = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "Android";
  if ((/iPad|iPhone|iPod/.test(ua) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && 
      !(window as any).MSStream) return "iOS";
  return "Other";
};

const AppContent = () => {
  const isMobile = useIsMobile();
  
  // Add viewport height fix for mobile browsers
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);

    // Apply OS specific classes
    const mobileOS = getMobileOS();
    if (mobileOS === "iOS") {
      document.body.classList.add("ios-device");
    } else if (mobileOS === "Android") {
      document.body.classList.add("android-device");
    }

    // Fix for Chrome mobile pull-to-refresh
    document.body.style.overscrollBehavior = "none";
    
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, []);

  return (
    <div 
      className="min-h-screen flex flex-col w-full overflow-x-hidden mobile-height-fix" 
      style={{ minHeight: "calc(var(--vh, 1vh) * 100)" }}
    >
      <Toaster />
      <Sonner position={isMobile ? "bottom-center" : "top-right"} closeButton={true} />
      <PWAInstallPrompt />
      {isMobile && <MobileBrowser />}
      <div className={isMobile ? 'pt-12' : ''}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/collaborate/:docId" element={<CollaborativeEditor />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/legal-guides" element={<LegalGuides />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
